import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;
  #lastSuccessfulPickerMarkup;
  #lastSuccessfulVariantId;
  #lastKnownAddToCartDisabled;

  constructor() {
    super();
    this.variantChanged = this.variantChanged.bind(this);
  }

  connectedCallback() {
    this.#lastSuccessfulPickerMarkup = this.innerHTML;
    this.#lastSuccessfulVariantId = new URL(window.location.href).searchParams.get('variant');
    this.rememberAddToCartState();
    this.addEventListener('change', this.variantChanged);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.variantChanged);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (!this.#abortController) {
      this.#lastSuccessfulPickerMarkup = this.innerHTML;
      this.#lastSuccessfulVariantId = new URL(window.location.href).searchParams.get('variant');
      this.rememberAddToCartState();
    }

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    const focusedOptionValueId =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]?.dataset.optionValueId
        : event.target.dataset.optionValueId;
    const requestUrl = this.buildRequestUrl();

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: focusedOptionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(requestUrl, focusedOptionValueId);

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl, focusedOptionValueId) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    fetch(requestUrl, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch product variant: HTTP ${response.status}`);
        }

        return response.text();
      })
      .then((responseText) => {
        if (this.#abortController !== abortController || abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newVariantPickerSource = html.querySelector(this.tagName.toLowerCase());

        if (!newVariantPickerSource) {
          throw new Error('Variant picker not found in product response');
        }

        // Récupère les données JSON de la variante depuis la réponse
        const textContent = newVariantPickerSource.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) {
          throw new Error('Variant data not found in product response');
        }

        const variant = JSON.parse(textContent);
        const selectedOption = newVariantPickerSource.querySelector(
          'select option[selected], fieldset input:checked',
        );
        const selectedOptionId = selectedOption?.dataset.optionValueId;

        if (!selectedOptionId) {
          throw new Error('Selected variant option not found in product response');
        }

        if (
          this.closest('product-form') &&
          !html.querySelector('[data-ref="add-to-cart-button-container"]') &&
          !html.querySelector('.product-form__pending')
        ) {
          throw new Error('Add to cart state not found in product response');
        }

        // Met à jour seulement le variant picker
        this.updateVariantPicker(html);

        // Déclenche un événement pour notifier la mise à jour de la variante
        this.dispatchEvent(
          new VariantUpdatedEvent(variant, selectedOptionId, {
            html,
            productId: this.dataset.productId ?? '',
          }),
        );

        this.#lastSuccessfulPickerMarkup = this.innerHTML;
        this.#lastSuccessfulVariantId = new URL(window.location.href).searchParams.get('variant');
        this.rememberAddToCartState();
        this.restoreFocus(focusedOptionValueId);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || this.#abortController !== abortController) return;

        this.restoreLastSuccessfulState(focusedOptionValueId);
        console.error(error);
      })
      .finally(() => {
        if (this.#abortController === abortController) {
          this.#abortController = null;
        }
      });
  }

  rememberAddToCartState() {
    const addToCartButton = this.closest('product-form')?.querySelector('[data-ref="add-to-cart-button"]');
    this.#lastKnownAddToCartDisabled = addToCartButton?.disabled;
  }

  restoreLastSuccessfulState(focusedOptionValueId) {
    if (this.#lastSuccessfulPickerMarkup != null) {
      this.innerHTML = this.#lastSuccessfulPickerMarkup;
    }

    const addToCartButton = this.closest('product-form')?.querySelector('[data-ref="add-to-cart-button"]');
    if (addToCartButton && typeof this.#lastKnownAddToCartDisabled === 'boolean') {
      addToCartButton.disabled = this.#lastKnownAddToCartDisabled;
    }

    this.restoreFocus(focusedOptionValueId);

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    if (this.#lastSuccessfulVariantId) {
      url.searchParams.set('variant', this.#lastSuccessfulVariantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  restoreFocus(optionValueId) {
    if (!optionValueId) return;

    const optionValue = Array.from(this.querySelectorAll('[data-option-value-id]')).find(
      (element) => element.dataset.optionValueId === optionValueId,
    );
    const focusTarget = optionValue instanceof HTMLOptionElement ? optionValue.closest('select') : optionValue;
    focusTarget?.focus({ preventScroll: true });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
